import EventType from 'ol/events/EventType.js';
import PointerInteraction, {Options} from 'ol/interaction/Pointer.js';
import { Pixel } from 'ol/pixel';
import { Renderer } from './Renderer';
import * as _ from 'lodash'
import WaypointFeature from './WaypointFeature';
import BaseEvent from 'ol/events/Event';
import { GeometryHelpers } from './geometryhelpers';
import WaypointTooltip from './WaypointTooltip';
import { Source } from 'ol/source';
import { Layer } from 'ol/layer';
import WaypointLayer from './WaypointLayer';
import { AtPixelOptions } from 'ol/PluggableMap';
import IsWaypointSelectedDelegate from './IsWaypointSelectedDelegate';
import SetDefaultCursorDelegate from './SetDefaultCursorDelegate';
import IsDraggingWaypointDelegate from './IsDraggingWaypointDelegate';
import { MapBrowserEvent } from 'ol';
import EventRememberingPointerInteraction from './EventRememberingPointerInteraction';
import WaypointOptions from './WaypointOptions';

export interface EditWaypointOptions {
    renderer: Renderer<any>;
}

export enum WaypointEvents {
    WaypointSelected = 'waypointselected',
    WaypointDeselected = 'waypointdeselected'
}

export class WaypointSelectedEvent extends BaseEvent {
    waypointSelected: WaypointFeature
    constructor(waypointSelected: WaypointFeature) {
        super(WaypointEvents.WaypointSelected)
        this.waypointSelected = waypointSelected
    }
}

export default class EditWaypoint extends EventRememberingPointerInteraction implements IsDraggingWaypointDelegate {
    options: EditWaypointOptions
    waypointTooltip: WaypointTooltip
    setDefaultCursorDelegate: SetDefaultCursorDelegate
    constructor(options: EditWaypointOptions, isWaypointSelected: IsWaypointSelectedDelegate, setDefaultCursor: SetDefaultCursorDelegate, pointerOptions?: Options) {
        super(pointerOptions)
        this.options = options
        this.waypointTooltip = new WaypointTooltip(isWaypointSelected, this)
        this.setDefaultCursorDelegate = setDefaultCursor
    }

    isDraggingWaypoint() {
        return !!this.waypointDragging
    }

    waypointDragging: WaypointFeature|undefined // or TO drag
    pickupLocation: Pixel|undefined
    isDragging = false

    setCursor(cursorType: string) {
        const targetElement = this.options.renderer.map.getTargetElement();
        if(targetElement) { // it's sometimes null, not sure why
            targetElement.style.cursor = cursorType
        }
    }

    handleEvent(event: MapBrowserEvent<any>) {
        if (event.originalEvent.type === EventType.CONTEXTMENU) {
            // Avoid context menu for long taps when drawing on mobile
            event.preventDefault();
        }

        if(event.type === 'singleclick') {
            return this.handleSingleClick(event)
        } else if(event.type === 'pointermove') {
            return this.handlePointerMove(event)
        } else if(event.type === 'pointerdown') {
            return this.handlePointerDown(event)
        } else if(event.type === 'pointerup') {
            return this.handlePointerUp(event)
        }
        return super.handleEvent(event)
    }

    handlePointerDown(event: MapBrowserEvent<any>) {
        this.waypointDragging = this.options.renderer.map.getFeaturesAtPixel(event.pixel, WaypointOptions.atPixelOptions).find((f: any) => f instanceof WaypointFeature) as WaypointFeature
        if(this.waypointDragging) {
            this.waypointTooltip.hide(event.map)
            this.options.renderer.mapInstanceState.draggingWaypoint = true
            this.pickupLocation = event.pixel
            return false // < prevents other interactions handling it
        }
        return true
    }

    handlePointerUp(event: MapBrowserEvent<any>) {
        this.waypointDragging = undefined
        this.pickupLocation = undefined
        if(this.isDragging) {
            this.setCursor('grab')
        }
        this.isDragging = false
        this.options.renderer.mapInstanceState.draggingWaypoint = false
        return true
    }

    getMovingCoordinate(event: MapBrowserEvent<any>) {
        const fewPixelsDown = [event.pixel[0], event.pixel[1] + 20] // this shows it a bit down from the cursor, so the point is visible
        return this.options.renderer.map.getCoordinateFromPixel(fewPixelsDown)
    }

    isMouseOnWaypoint = false
    handlePointerMove(event: MapBrowserEvent<any>) {
        if(this.pickupLocation && this.waypointDragging && !this.isDragging && !GeometryHelpers.isVeryClose(this.pickupLocation, event.pixel)) {
            this.isDragging = true
            this.setCursor('grabbing')
        } else if (this.isDragging && this.waypointDragging) {
            this.waypointDragging.getGeometry().setCoordinates(this.getMovingCoordinate(event))
        } else {
            const wpt = this.options.renderer.map.getFeaturesAtPixel(event.pixel, WaypointOptions.atPixelOptions).find((f: any) => f instanceof WaypointFeature) as WaypointFeature
            if (wpt) {
                this.setCursor('grab')
                if(!this.isMouseOnWaypoint) {
                    this.waypointTooltip.show(wpt, event)
                    this.isMouseOnWaypoint = true
                }
            } else {
                this.setDefaultCursorDelegate.setDefaultCursor(event)
                this.isMouseOnWaypoint = false // prevent it appearing right after we drop it
            }
        }
        return true
    }

    handleSingleClick(event: MapBrowserEvent<any>) {
        // if there's already a way point at this location, select it, rather than adding a new one.
        // (if they actually want another one there, the options are either: create it further away and move it in, grow a pair, or jog on.)
        const waypointHere = this.options.renderer.map.getFeaturesAtPixel(event.pixel, WaypointOptions.atPixelOptions).find((f: any) => f instanceof WaypointFeature) as WaypointFeature
        if(waypointHere) {
            this.waypointTooltip.hide(event.map)
            this.dispatchEvent(new WaypointSelectedEvent(waypointHere))
        } else {
            this.dispatchEvent(WaypointEvents.WaypointDeselected)
        }
        return true
    }
}
